import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Book, Lock, BookOpen, Map, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Library = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, token } = useAuth();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progressMap, setProgressMap] = useState({});

  useEffect(() => {
    if (!isAuthenticated()) { navigate('/login'); return; }
    fetchPublishedBooks();
  }, []);

  const fetchPublishedBooks = async () => {
    try {
      const headers = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/api/books/published`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setBooks(data.books);
        // Fetch progress for each book
        fetchAllProgress(data.books, headers);
      }
    } catch (e) {
      console.error('Error fetching books:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllProgress = async (bookList, headers) => {
    const map = {};
    await Promise.all(
      bookList.map(async (b) => {
        try {
          const res = await fetch(`${API_URL}/api/reading/progress/${b.book_id}`, { headers, credentials: 'include' });
          if (res.ok) {
            const data = await res.json();
            if (data.chapters_read && data.chapters_read.length > 0) {
              map[b.book_id] = data;
            }
          }
        } catch (_) { /* silent */ }
      })
    );
    setProgressMap(map);
  };

  const getUserTier = () => user?.subscription_tier || 'seeker';

  const canAccessBook = (book) => {
    const t = getUserTier();
    if (t === 'scholar') return true;
    if (t === 'berean' && ['berean', 'seeker'].includes(book.access_tier || 'seeker')) return true;
    if (t === 'seeker' && (book.access_tier || 'seeker') === 'seeker') return true;
    return false;
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'scholar': return 'bg-purple-600';
      case 'berean': return 'bg-blue-600';
      case 'seeker': return 'bg-green-600';
      default: return 'bg-gray-600';
    }
  };

  const handleBookClick = (book) => {
    if (canAccessBook(book)) {
      const progress = progressMap[book.book_id];
      const chapter = progress?.last_chapter || 1;
      navigate(`/ebooks/${book.book_id}/${chapter}`);
    } else {
      navigate('/subscriptions');
    }
  };

  const handleResumeClick = (bookId, lastChapter) => {
    navigate(`/ebooks/${bookId}/${lastChapter}`);
  };

  // Find the most recently read book for the "Continue Reading" banner
  const activeBooks = books.filter(b => progressMap[b.book_id] && canAccessBook(b));

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto mb-4" />
          <p>Loading library...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <BookOpen className="h-16 w-16 mx-auto mb-6 text-amber-500" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Your Digital Library</h1>
            <p className="text-xl text-gray-300 mb-6">Access your collection of spiritual teachings and biblical analysis</p>
            <div className="flex flex-wrap items-center justify-center gap-3 mb-4">
              <Button data-testid="btn-mindmap" onClick={() => navigate('/mindmap')} className="bg-amber-600 hover:bg-amber-700 text-white">
                <Map className="h-4 w-4 mr-2" /> Explore Knowledge Map
              </Button>
            </div>
            <div className="inline-flex items-center gap-2 bg-white/10 px-6 py-3 rounded-lg">
              <span className="text-sm text-gray-300">Your Plan:</span>
              <Badge className={getTierBadgeColor(getUserTier())}>
                {getUserTier().charAt(0).toUpperCase() + getUserTier().slice(1)}
              </Badge>
              <Button variant="ghost" size="sm" onClick={() => navigate('/subscriptions')} className="text-amber-400 hover:text-amber-300">
                Upgrade
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Continue Reading Banner */}
      {activeBooks.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200">
          <div className="container mx-auto px-4 py-6">
            <h2 className="text-sm font-semibold text-amber-500 uppercase tracking-wider mb-4" data-testid="resume-section-title">
              Continue Reading
            </h2>
            <div className="space-y-3">
              {activeBooks.map((book) => {
                const prog = progressMap[book.book_id];
                const chaptersRead = prog?.chapters_read?.length || 0;
                const totalChapters = book.chapter_count || 1;
                const pct = Math.round((chaptersRead / totalChapters) * 100);

                return (
                  <div
                    key={book.book_id}
                    className="flex items-center gap-4 bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handleResumeClick(book.book_id, prog.last_chapter)}
                    data-testid={`resume-card-${book.book_id}`}
                  >
                    {/* Book cover thumbnail */}
                    <div className="flex-shrink-0 w-12 h-16 rounded-md overflow-hidden bg-gradient-to-br from-amber-200 to-amber-100 flex items-center justify-center">
                      {book.cover_url ? (
                        <img src={book.cover_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Book className="w-6 h-6 text-amber-400" />
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-800 text-sm truncate">{book.title}</h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Chapter {prog.last_chapter} of {totalChapters} &middot; {chaptersRead} read
                      </p>
                      {/* Progress bar */}
                      <div className="mt-2 w-full h-1.5 rounded-full bg-amber-100">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${pct}%`,
                            background: pct >= 100 ? '#16a34a' : 'linear-gradient(90deg, #d97706, #f59e0b)',
                          }}
                          data-testid={`resume-progress-${book.book_id}`}
                        />
                      </div>
                    </div>

                    {/* Resume button */}
                    <Button
                      size="sm"
                      className="flex-shrink-0 bg-amber-600 hover:bg-amber-700 text-white gap-1"
                      data-testid={`resume-btn-${book.book_id}`}
                      onClick={(e) => { e.stopPropagation(); handleResumeClick(book.book_id, prog.last_chapter); }}
                    >
                      Resume <ArrowRight className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Books Grid */}
      <div className="container mx-auto px-4 py-12">
        {books.length === 0 ? (
          <div className="max-w-2xl mx-auto text-center py-16">
            <Book className="h-24 w-24 text-gray-400 mx-auto mb-6" />
            <h2 className="text-2xl font-bold mb-4">No Books Available Yet</h2>
            <p className="text-gray-600 mb-6">Check back soon for new releases from Trump of the Lord Ministries</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {books.map((book) => {
              const hasAccess = canAccessBook(book);
              const prog = progressMap[book.book_id];
              const chaptersRead = prog?.chapters_read?.length || 0;
              const totalChapters = book.chapter_count || 1;
              const pct = chaptersRead > 0 ? Math.round((chaptersRead / totalChapters) * 100) : 0;

              return (
                <Card
                  key={book.book_id}
                  data-testid={`book-card-${book.book_id}`}
                  className={`overflow-hidden hover:shadow-xl transition-all cursor-pointer ${!hasAccess ? 'opacity-75' : ''}`}
                  onClick={() => handleBookClick(book)}
                >
                  {/* Cover */}
                  <div className="relative h-64 bg-gradient-to-br from-amber-100 to-amber-50">
                    {book.cover_url ? (
                      <img src={book.cover_url} alt={book.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <Book className="h-20 w-20 text-amber-300" />
                      </div>
                    )}

                    {!hasAccess && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                        <div className="text-center">
                          <Lock className="h-12 w-12 text-white mx-auto mb-2" />
                          <p className="text-white font-semibold text-sm">
                            {(book.access_tier || 'seeker').charAt(0).toUpperCase() + (book.access_tier || 'seeker').slice(1)} Required
                          </p>
                        </div>
                      </div>
                    )}

                    <Badge className={`absolute top-2 right-2 ${getTierBadgeColor(book.access_tier || 'seeker')}`}>
                      {(book.access_tier || 'seeker').charAt(0).toUpperCase() + (book.access_tier || 'seeker').slice(1)}
                    </Badge>

                    {/* Completion badge */}
                    {pct >= 100 && (
                      <div className="absolute top-2 left-2 bg-green-600 text-white text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Complete
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-6">
                    <h3 className="font-bold text-lg mb-1 line-clamp-2">{book.title}</h3>
                    {book.subtitle && <p className="text-sm text-gray-600 mb-2 line-clamp-1">{book.subtitle}</p>}
                    <p className="text-sm text-gray-500 mb-3">by {book.author}</p>

                    {book.description && <p className="text-sm text-gray-600 line-clamp-3 mb-4">{book.description}</p>}

                    {/* Chapter count + progress */}
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                      <span>{book.chapter_count || 0} chapters</span>
                      {pct > 0 && <span className="text-amber-700 font-medium">{pct}% read</span>}
                    </div>

                    {/* Progress bar on card */}
                    {pct > 0 && (
                      <div className="w-full h-1 rounded-full bg-gray-200 mb-4">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${pct}%`,
                            background: pct >= 100 ? '#16a34a' : '#d97706',
                          }}
                          data-testid={`card-progress-${book.book_id}`}
                        />
                      </div>
                    )}

                    <Button
                      className={`w-full ${hasAccess ? 'bg-amber-600 hover:bg-amber-700' : 'bg-gray-400 hover:bg-gray-500'}`}
                      data-testid={`btn-read-${book.book_id}`}
                      onClick={(e) => { e.stopPropagation(); handleBookClick(book); }}
                    >
                      {!hasAccess ? 'Upgrade to Read' : pct > 0 ? 'Continue Reading' : 'Read Now'}
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Library;
